import { OnInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { DxFormComponent } from 'devextreme-angular';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, map, of, switchMap } from 'rxjs';
import { checkIcon, closeIcon, lockIcon, starIcon, usersIcon, warningIcon } from 'src/app/app-icons';
import { StandardTrainingCategoryViewModel } from 'src/app/models/standard-training-category/standard-training-category-view.model';
import { SubscriptionProviderCategorySummaryModel } from 'src/app/models/subscription/subscription-prodiver-category-summary.model';
import { SubscriptionProviderCategoryUpdateModel } from 'src/app/models/subscription/subscription-provider-category-update.model';
import { TrainingCategoryCreationAndUpdateModel } from 'src/app/models/training-category/training-category-creation-and-update.model';
import { TrainingCategoryFullModel, TrainingCategoryModel } from 'src/app/models/training-category/training-category.model';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { FileUtilService } from 'src/app/services/file-util.service';
import { LoadingService } from 'src/app/services/loading.service';
import { SubscriptionSettingsService } from 'src/app/services/subscriptions/subscription-settings.service';
import { StandardTrainingCategoryService } from 'src/app/services/training-categories/standard-training-category.service';
import { TrainingCategoryService } from 'src/app/services/training-categories/training-category.service';
import { TrainingGroupService } from 'src/app/services/training-group.service';
import { UserContextService } from 'src/app/services/user-context.service';

@Component({
  selector: 'app-subscription-settings-modal',
  standalone: false,
  templateUrl: './subscription-settings-modal.component.html',
  styleUrl: './subscription-settings-modal.component.scss'
})
export class SubscriptionSettingsModalComponent implements OnInit {
  @ViewChild('settingsForm', { static: true })
  settingsForm!: DxFormComponent;

  @Input() showModal = false;
  @Output() showModalChange = new EventEmitter<boolean>();

  @Input() title!: string;
  @Input() subscriptionId!: string;
  
  @Output() onSend = new EventEmitter();

  trainingCategories: TrainingCategoryFullModel[] = [];
  standardCategories: StandardTrainingCategoryViewModel[] = [];
  trainingGroups: TrainingGroupModel[] = [];

  subscriptionProviderCategories: SubscriptionProviderCategorySummaryModel[] = [];
  isLoaded: boolean = false;

  providers: SubscriptionProvider[] = [];

  get matchedProviders(): SubscriptionProvider[] {
    return this.providers.filter(x => x.consumerTrainingCategories.length);
  }

  get attentionProviders(): SubscriptionProvider[] {
    return this.providers.filter(x => !x.consumerTrainingCategories.length && x.enabled);
  }

  get unmatchedProviders(): SubscriptionProvider[] {
    return this.providers.filter(x => !x.consumerTrainingCategories.length && !x.enabled);
  }

  get overviewTranslationKey(): string {
    if (this.providers.length === 0) {
      return 'SUBSCRIPTION-SETTINGS.OVERVIEW.NO-SUBSCRIPTIONS';
    }

    if (this.attentionProviders.length === 0) {
      return 'SUBSCRIPTION-SETTINGS.OVERVIEW.FULL-MATCH';
    }

    if (this.matchedProviders.length === 0) {
      return 'SUBSCRIPTION-SETTINGS.OVERVIEW.NO-MATCH';
    }

    return 'SUBSCRIPTION-SETTINGS.OVERVIEW.PARTIAL-MATCH';
  }

  get saveText(): string {
    const saveTextStr = this.translateService.instant(
      'COMMON.CONFIRM-AND-SAVE'
    );

    const allCount = this.providers.length;
    const enabledCount = this.providers.filter(x => x.enabled).length;

    if (allCount === enabledCount) {
      return saveTextStr;
    }

    const countText = this.translateService.instant(
      'SUBSCRIPTION-SETTINGS.ENABLED-COUNT',
      {
        enabled: enabledCount,
        total: allCount
      });

    return `${saveTextStr} · ${countText}`;
  }

  constructor(
    private translateService: TranslateService,
    private loadingService: LoadingService,
    private toastrService: ToastrService,
    private subscriptionSettingsService: SubscriptionSettingsService,
    private trainingCategoryService: TrainingCategoryService,
    private standardTrainingCategoryService: StandardTrainingCategoryService,
    private trainingGroupService: TrainingGroupService,
    private sanitized: DomSanitizer,
    private fileUtilService: FileUtilService,
    private userContext: UserContextService
  ) {
  }

  ngOnInit(): void {
    this.loadingService.load(
      forkJoin({
        trainingCategories: this.trainingCategoryService.getAllTrainingCategoriesFull(),
        standardCategories: this.standardTrainingCategoryService.getAllStandardTrainingCategories(
          this.userContext.currentPersonCulture ?? 'en'
        ),
        trainingGroups: this.trainingGroupService.getAllTrainingGroups(),
        subscriptionProviderCategories: 
          this.subscriptionSettingsService.getSubscriptionProviderCategorySummaries(this.subscriptionId)
      })
    ).subscribe(({ 
      trainingCategories, standardCategories, trainingGroups, subscriptionProviderCategories 
    }) => {
      this.trainingCategories = trainingCategories;
      this.trainingGroups = trainingGroups;
      this.subscriptionProviderCategories = subscriptionProviderCategories;

      this.standardCategories = standardCategories.map(x => ({
        id: x.id,
        name: x.name ?? '',
        description: x.description ?? '',
        descriptionExtended: x.descriptionExtended ?? '',
        type: x.type,
        parentId: x.parentId,
        imageFile: x.imageFile
      }));

      this.setSubscriptionCategories(subscriptionProviderCategories);
      this.isLoaded = true;
    }); 
  }

  getCheckIcon() {
    return this.sanitized.bypassSecurityTrustHtml(checkIcon);
  }

  getWarningIcon() {
    return this.sanitized.bypassSecurityTrustHtml(warningIcon);
  }

  getStarIcon() {
    return this.sanitized.bypassSecurityTrustHtml(starIcon);
  }

  getUsersIcon() {
    return this.sanitized.bypassSecurityTrustHtml(usersIcon);
  }

  getCloseIcon() {
    return this.sanitized.bypassSecurityTrustHtml(closeIcon);
  }

  getLockIcon() {
    return this.sanitized.bypassSecurityTrustHtml(lockIcon);
  }

  getConsumerTrainingCategoryIds(
    item: SubscriptionProvider
  ): string[] {
    return item.consumerTrainingCategories.map(x => x.id);
  }

  getConsumerCategoryNames(item: SubscriptionProvider): string {
    return item.consumerTrainingCategories
      .map(x => x.name)
      .join(', ');
  }

  isAutoMatched(item: SubscriptionProvider): boolean {
    if(item.consumerTrainingCategories.length !== 1) return false;

    const consumerCategory = item.consumerTrainingCategories[0]; // take first if standard
    const publisherStandardCategoryId = item.publisherStandardCategory.id;

    return publisherStandardCategoryId === consumerCategory.standardTrainingCategoryId
      || publisherStandardCategoryId === consumerCategory.associatedStandardTrainingCategoryId;
  }

  skipProviderCategory(item: SubscriptionProvider): void {
    item.enabled = false;
  }

  restoreProviderCategory(item: SubscriptionProvider): void {
    item.enabled = true;
  }

  unmatchCategory(item: SubscriptionProvider): void {
    item.consumerTrainingCategories = [];
    item.consumerTrainingGroups = null;
  }

  setSubscriptionCategories(
    subscriptionProviderCategories: SubscriptionProviderCategorySummaryModel[]
  ) {
    this.providers = subscriptionProviderCategories.map(x => ({
      publisherStandardCategory: this.standardCategories.find(
        sc => sc.id === x.providerTrainingCategory.standardTrainingCategoryId
          || sc.id === x.providerTrainingCategory.associatedStandardTrainingCategoryId
      )!,
      publisherTrainingCategory: x.providerTrainingCategory,
      consumerTrainingCategories: x.consumerTrainingCategories,
      consumerTrainingGroups: x.consumerTrainingGroups,
      enabled: x.enabled
    }));
  }

  private selectedTrainingCategories: TrainingCategoryFullModel[] = [];

  onSelectionChanged(e: any): void {
    this.selectedTrainingCategories = e.component
      .getSelectedNodes()
      .map((n: any) => n.itemData);
  }

  applySelection(
    item: SubscriptionProvider,
    dropDown: any, treeView: any
  ): void {
    item.consumerTrainingCategories = this.selectedTrainingCategories
      .map(tc => tc.trainingCategory);
   
    const trainingGroups = new Map<string, TrainingGroupModel>();

    for (const category of this.selectedTrainingCategories) {
      for (const group of category.associatedTrainingGroups ?? []) {
        trainingGroups.set(group.id, group);
      }
    }

    item.consumerTrainingGroups = [...trainingGroups.values()];

    this.selectedTrainingCategories = [];
    treeView.instance.unselectAll();
    dropDown.instance.close();
  }

  cancelSelection(dropDown: any, treeView: any): void {
    this.selectedTrainingCategories = [];
    treeView.instance.unselectAll();
    dropDown.instance.close();
  }

  onTrainingCategoriesChanged(
    item: SubscriptionProvider, e: any
  ): void {
    const selectedCategories: any[] = e.component
      .getSelectedNodes()
      .map((n: any) => n.itemData);

    item.consumerTrainingCategories = selectedCategories
      .map(x => x.trainingCategory);

    const groups = new Map<string, TrainingGroupModel>();

    for (const category of selectedCategories) {
      for (const group of category.associatedTrainingGroups ?? []) {
        groups.set(group.id, group);
      }
    }

    item.consumerTrainingGroups = [...groups.values()];
  }

  initTree(e: any, item: SubscriptionProvider) {
    const consumerTrainingCategoryIds = 
      this.getConsumerTrainingCategoryIds(item);

    for (const id of consumerTrainingCategoryIds) {
      e.component.selectItem(id);
    }
  }

  onFormSubmit() {
    const models: SubscriptionProviderCategoryUpdateModel[] = 
      this.patchUpdateModels(this.providers, this.subscriptionProviderCategories);
    
    this.loadingService.load(
      this.subscriptionSettingsService.updateSubscriptionProviderCategories(
        this.subscriptionId, models
      )
    ).subscribe(() => {
      this.toastrService.success(
        this.translateService.instant(
          'SUBSCRIPTION-SETTINGS.SUBSCRIPTION-SETTINGS-SAVED-SUCCESSFULLY'
        )
      );
      this.onSend.emit();
      this.close(); 
    });
  }

  private patchUpdateModels(
    providers: SubscriptionProvider[],
    subscriptionProviderCategories: SubscriptionProviderCategorySummaryModel[]
  ): SubscriptionProviderCategoryUpdateModel[] {

    return providers
      .map(provider => {
        const subscriptionProviderCategory = subscriptionProviderCategories.find(
          x => x.providerTrainingCategory.id === provider.publisherTrainingCategory.id
        );

        if (!subscriptionProviderCategory) {
          return null;
        }

        const model: SubscriptionProviderCategoryUpdateModel = {
          subscriptionProviderCategoryId:
            subscriptionProviderCategory.subscriptionProviderCategoryId,

          consumerTrainingCategoryIds:
            provider.consumerTrainingCategories.map(x => x.id),

          enabled: provider.enabled
        };

        return model;
      })
      .filter(x => x !== null);
  }

  close() {
    this.showModal = false;
    this.showModalChange.emit(this.showModal);
  }

  addFromStandard(item: SubscriptionProvider): void {
    const standardCategory = item.publisherStandardCategory;

    const categoryModel: TrainingCategoryCreationAndUpdateModel = {
      standardTrainingCategoryId: standardCategory.id,
      associatedStandardTrainingCategoryId: null,

      name: standardCategory.name,
      description: standardCategory.description,
      descriptionExtended: standardCategory.descriptionExtended,

      image: null,
      includeInLearningMatrix: true,
      associatedTrainingGroupIds: []
    };

    const imageUrl = standardCategory.imageFile?.fileUrl;

    const image$ = imageUrl
      ? this.fileUtilService.avatarUrlToBase64(imageUrl).pipe(
          map(base64 => {
            categoryModel.image = base64;
            return null;
          })
        )
      : of(null);

    this.loadingService.load(
      image$.pipe(
        switchMap(() => this.trainingCategoryService.addTrainingCategory(categoryModel)),
        switchMap(createdCategory => {
            const subscriptionProviderCategory = this.subscriptionProviderCategories.find(
              x => x.providerTrainingCategory.id === item.publisherTrainingCategory.id
            );

            if(!subscriptionProviderCategory) return of(null);

            const subscriptionModel: SubscriptionProviderCategoryUpdateModel = {
              subscriptionProviderCategoryId: 
                subscriptionProviderCategory.subscriptionProviderCategoryId,
              consumerTrainingCategoryIds: [createdCategory.trainingCategory.id],
              enabled: item.enabled
            };

            return this.subscriptionSettingsService.updateSubscriptionProviderCategories(
              this.subscriptionId, [subscriptionModel]
            ).pipe(
              map(() => createdCategory)
            )
        })
      )
    ).subscribe(createdCategory => {
      if(!createdCategory) return;

      item.consumerTrainingCategories = [createdCategory.trainingCategory];
      item.consumerTrainingGroups = createdCategory.associatedTrainingGroups;
    });
  }
}

interface SubscriptionProvider {
  publisherStandardCategory: StandardTrainingCategoryViewModel;
  publisherTrainingCategory: TrainingCategoryModel;
  consumerTrainingCategories: TrainingCategoryModel[];
  consumerTrainingGroups: TrainingGroupModel[] | null;
  enabled: boolean;
}
